import { byLocale, localeLabel } from "@/lib/locale";
/**
 * Single source of truth for all professional category values.
 *
 * Every surface — search filters, home page, footer, application form,
 * admin approval pipeline — must import from here. Never hardcode
 * category strings elsewhere in the codebase.
 */

// ── Canonical directory categories (match DB values exactly) ────────────────
//
// ⚠ ADDING ONE REQUIRES A MIGRATION. This list is duplicated into two Postgres
// CHECK constraints — `professionals_category_check` and
// `requests_category_check` (which also allows the requests-only "Other").
// Adding a value here alone compiles, type-checks, passes the taxonomy test in
// `__tests__/categories.test.ts`, and renders correctly, then fails at WRITE
// time: admin approval can't set the new category on `professionals`, and the
// public /request form offers it (REQUEST_CATEGORY_OPTIONS spreads this array)
// but the insert is rejected. Widen the constraints first, then ship the code —
// see `20260815082353_add_insurance_relocation_categories.sql` for the pattern.
//
// The unit test cannot catch this: it has no database. Same trap applies to the
// province and availability vocabularies, which are likewise mirrored into
// CHECK constraints.

export const DIRECTORY_CATEGORIES = [
  "Legal",
  "Finance",
  "Property",
  "Business",
  "Visa",
  "Audit",
  "Notary",
  "Translation",
  "Insurance",
  "Relocation",
  // 11th, 2026-08-27. PROMOTED 2026-09-20 (owner): now on the home pills,
  // footer, llms.txt and the chat (`permits_licences`); the note below is the
  // history. Work permits, business licences, driving licences,
  // construction permits. It exists because the taxonomy had no shelf for a
  // permit or licence topic that is NOT an immigration matter: the motorbike
  // guide sat under "Visa", and the chat then offered visa and legal
  // professionals under a complete factual answer about road licences. A shelf
  // label was being read as evidence of what the reader needs.
  //
  // Kept OUT of CATEGORY_HOME_PILLS and CATEGORY_FOOTER_LINKS on purpose until
  // it holds approved professionals (owner, 2026-08-27) — a discovery link to
  // an empty category is a dead end. Everything that gates on live counts
  // (autocomplete suggestions, the chat offers, the guide funnel CTA) turns it
  // on by itself the day the first one is approved.
  "Permit",
  // 12th, 2026-09-13 (owner). Serviced and virtual offices, coworking, office
  // leasing. Same discoverability rule as Permit: kept OUT of the home pills
  // and footer links until it holds approved professionals; everything gated on
  // live counts turns it on by itself.
  "Office",
  // 13th, 2026-09-19 (owner). Payroll, social insurance, labour contracts, HR
  // outsourcing, employer of record, recruitment. PROMOTED 2026-09-20 with
  // Permit (home pills, footer, llms.txt, chat `hr_payroll`). Office alone
  // stays undiscoverable.
  "HR",
] as const;

export type DirectoryCategory = (typeof DIRECTORY_CATEGORIES)[number];

// ── Human-readable display labels (filter pills, headings, etc.) ────────────

export const CATEGORY_DISPLAY_LABELS: Record<DirectoryCategory, string> = {
  Legal:       "Legal",
  Finance:     "Finance & Accounting",
  Property:    "Property",
  Business:    "Business",
  Visa:        "Visa",
  Audit:       "Audit & Compliance",
  Notary:      "Notary & Legalization",
  Translation: "Translation & Interpretation",
  Insurance:   "Insurance",
  Relocation:  "Relocation",
  Permit:      "Permits & Licences",
  Office:      "Office & Workspace",
  HR:          "HR & Payroll",
};

// ── Localized category labels (MACHINE DRAFT — pending native reviewer) ──────
// The DB category values + slugs + hrefs are stable across locales; only the
// human-readable labels localize, mirroring the province data-label system.
// FR falls back to EN. The reviewer edits the *_VI maps below.

/** French display labels. Sentence case per `messages/fr-style-guide.md`; the
 *  glossary fixes "professionnel", "comptabilité", "fiscalité", "notaire". */
const CATEGORY_DISPLAY_LABELS_FR: Record<DirectoryCategory, string> = {
  Legal:       "Juridique",
  Finance:     "Finance et comptabilité",
  Property:    "Immobilier",
  Business:    "Entreprise",
  Visa:        "Visa",
  Audit:       "Audit et conformité",
  Notary:      "Notariat et légalisation",
  Translation: "Traduction et interprétation",
  Insurance:   "Assurance",
  Relocation:  "Déménagement",
  Permit:      "Permis et licences",
  Office:      "Bureaux et espaces de travail",
  HR:          "RH et paie",
};

const CATEGORY_DISPLAY_LABELS_VI: Record<DirectoryCategory, string> = {
  Legal:       "Pháp lý",
  Finance:     "Tài chính & Kế toán",
  Property:    "Bất động sản",
  Business:    "Kinh doanh",
  Visa:        "Visa",
  Audit:       "Kiểm toán & Tuân thủ",
  Notary:      "Công chứng & Hợp pháp hóa",
  Translation: "Biên dịch & Phiên dịch",
  Insurance:   "Bảo hiểm",
  Relocation:  "Hỗ trợ định cư",
  Permit:      "Giấy phép",
  Office:      "Văn phòng làm việc",
  HR:          "Nhân sự & Tiền lương",
};

/** Simplified Chinese display labels, machine draft per
 *  `messages/zh-Hans-style-guide.md` (glossary: 公证, 搬迁服务, 许可与执照). */
const CATEGORY_DISPLAY_LABELS_ZH_HANS: Record<DirectoryCategory, string> = {
  Legal:       "法律",
  Finance:     "财务与会计",
  Property:    "房地产",
  Business:    "企业服务",
  Visa:        "签证",
  Audit:       "审计与合规",
  Notary:      "公证与认证",
  Translation: "翻译与口译",
  Insurance:   "保险",
  Relocation:  "搬迁服务",
  Permit:      "许可与执照",
  Office:      "办公室与办公空间",
  HR:          "人力资源与薪资",
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const CATEGORY_DISPLAY_LABELS_ZH_HANT: Record<DirectoryCategory, string> = {
  Legal:       "法律",
  Finance:     "財務與會計",
  Property:    "房地產",
  Business:    "企業服務",
  Visa:        "簽證",
  Audit:       "審計與合規",
  Notary:      "公證與認證",
  Translation: "翻譯與口譯",
  Insurance:   "保險",
  Relocation:  "搬遷服務",
  Permit:      "許可與執照",
  Office:      "辦公室與辦公空間",
  HR:          "人力資源與薪資",
};

/** Locale-aware category display label (VI, FR and zh machine drafts). */
export function getCategoryLabel(cat: DirectoryCategory, locale: string): string {
  return byLocale(locale, {
    en: CATEGORY_DISPLAY_LABELS,
    vi: CATEGORY_DISPLAY_LABELS_VI,
    fr: CATEGORY_DISPLAY_LABELS_FR,
    "zh-Hans": CATEGORY_DISPLAY_LABELS_ZH_HANS,
    "zh-Hant": CATEGORY_DISPLAY_LABELS_ZH_HANT,
  })[cat];
}

// ── Insights (editorial) category → directory service category bridge ───────
// The Sanity editorial categories (keys below) are broader/different from the
// directory's SERVICE categories (DIRECTORY_CATEGORIES), so they can't be used
// directly as a `?category=` filter. This map bridges them so an article can
// funnel readers to relevant professionals + link to the right search page.
// The FIRST entry is the "primary" funnel target (used for the CTA link).
// Categories with no literal directory equivalent (Technology, Lifestyle) map to
// the closest-adjacent service pools. An empty array = no match → the related-pro
// row is hidden and the funnel falls back to the whole directory.
// EDIT THIS if the editorial taxonomy or DIRECTORY_CATEGORIES changes; keys
// must match the Sanity category `title` exactly.
export const INSIGHTS_CATEGORY_TO_SERVICE: Record<string, readonly DirectoryCategory[]> = {
  "Legal & Visa":   ["Legal", "Visa"],
  "Business & Tax": ["Business", "Finance"],
  "Real Estate":    ["Property"],
  "Technology":     ["Business", "Finance"], // business setup + tax/accounting
  "Lifestyle":      ["Visa", "Property"],    // relocation staples: visas + housing
  "Documents":      ["Legal", "Notary"],     // notarisation + legal drafting
};

/** Directory service categories an insights article should funnel to (by the
 *  article's editorial category title). Empty when there's no service match. */
export function insightsCategoryToServiceCategories(
  editorialTitle: string,
): readonly DirectoryCategory[] {
  return INSIGHTS_CATEGORY_TO_SERVICE[editorialTitle] ?? [];
}

// ── Slug ↔ DB value mapping (for case-insensitive URL param handling) ───────

const SLUG_TO_CATEGORY: Record<string, DirectoryCategory> = {
  legal:       "Legal",
  finance:     "Finance",
  property:    "Property",
  business:    "Business",
  visa:        "Visa",
  audit:       "Audit",
  notary:      "Notary",
  translation: "Translation",
  insurance:   "Insurance",
  relocation:  "Relocation",
  permit:      "Permit",
  office:      "Office",
  hr:          "HR",
};

/** Normalize a raw URL param (case-insensitive) to the canonical DB value. */
export function normalizeCategory(raw: string): DirectoryCategory | "" {
  if (!raw) return "";
  const slug = raw.toLowerCase().trim();
  return SLUG_TO_CATEGORY[slug] ?? "";
}

/** Return directory categories excluding a given primary (for secondary selection). */
export function getSecondaryOptions(primary: DirectoryCategory | ""): DirectoryCategory[] {
  if (!primary) return [...DIRECTORY_CATEGORIES];
  return DIRECTORY_CATEGORIES.filter((c) => c !== primary);
}

// ── Search page display headings ────────────────────────────────────────────

export interface CategoryHeading { pre: string; italic: string; post: string }

// The H1 names the PROFESSION and the COUNTRY in the words people search for
// ("lawyers in Vietnam"), not the shelf ("LEGAL & Corporate EXPERTS"), which
// nobody types. Owner, 2026-09-18. `seo/category-meta.ts` carries the matching
// <title> and description; keep the two saying the same thing.
export const CATEGORY_HEADINGS: Record<DirectoryCategory, CategoryHeading> = {
  Visa:        { pre: "VISA",              italic: "& Immigration",    post: "AGENTS IN VIETNAM" },
  Legal:       { pre: "LAWYERS",           italic: "& Legal Counsel",  post: "IN VIETNAM" },
  Finance:     { pre: "ACCOUNTANTS",       italic: "& Tax Advisors",   post: "IN VIETNAM" },
  Property:    { pre: "PROPERTY",          italic: "& Real Estate",    post: "EXPERTS IN VIETNAM" },
  // Shorter than its <title> on purpose: with "Business Setup" it ran to four
  // lines at 375px (measured). The title still carries both phrases.
  Business:    { pre: "COMPANY FORMATION", italic: "& Setup",          post: "IN VIETNAM" },
  Audit:       { pre: "AUDITORS",          italic: "& Compliance",     post: "FIRMS IN VIETNAM" },
  Notary:      { pre: "NOTARY",            italic: "& Legalization",   post: "SERVICES IN VIETNAM" },
  Translation: { pre: "TRANSLATORS",       italic: "& Interpreters",   post: "IN VIETNAM" },
  Insurance:   { pre: "INSURANCE",         italic: "& Risk",           post: "ADVISORS IN VIETNAM" },
  Relocation:  { pre: "RELOCATION",        italic: "& Moving",         post: "COMPANIES IN VIETNAM" },
  Permit:      { pre: "PERMIT",            italic: "& Licensing",      post: "SERVICES IN VIETNAM" },
  // PROVIDERS, not EXPERTS: this shelf is mostly operators and landlords.
  Office:      { pre: "OFFICE",            italic: "& Workspace",      post: "PROVIDERS IN VIETNAM" },
  HR:          { pre: "HR",                italic: "& Payroll",        post: "SERVICES IN VIETNAM" },
};

// VI restructures the heading: in Vietnamese "experts" leads, so pre = "CHUYÊN
// GIA" and the category name (italic) follows; post is empty. MACHINE DRAFT.
const CATEGORY_HEADINGS_VI: Record<DirectoryCategory, CategoryHeading> = {
  Visa:        { pre: "CHUYÊN GIA", italic: "Visa & Di trú",              post: "" },
  Legal:       { pre: "CHUYÊN GIA", italic: "Pháp lý & Doanh nghiệp",     post: "" },
  Finance:     { pre: "CHUYÊN GIA", italic: "Tài chính & Kế toán",        post: "" },
  Property:    { pre: "CHUYÊN GIA", italic: "Bất động sản",               post: "" },
  Business:    { pre: "CHUYÊN GIA", italic: "Thành lập doanh nghiệp",     post: "" },
  Audit:       { pre: "CHUYÊN GIA", italic: "Kiểm toán & Tuân thủ",       post: "" },
  Notary:      { pre: "CHUYÊN GIA", italic: "Công chứng & Hợp pháp hóa",  post: "" },
  Translation: { pre: "CHUYÊN GIA", italic: "Biên dịch & Phiên dịch",     post: "" },
  Insurance:   { pre: "CHUYÊN GIA", italic: "Bảo hiểm",                   post: "" },
  Relocation:  { pre: "CHUYÊN GIA", italic: "Hỗ trợ định cư",             post: "" },
  Permit:      { pre: "CHUYÊN GIA", italic: "Giấy phép",                   post: "" },
  Office:      { pre: "NHÀ CUNG CẤP", italic: "Văn phòng làm việc",        post: "" },
  HR:          { pre: "CHUYÊN GIA", italic: "Nhân sự & Tiền lương",       post: "" },
};

/** French search-page headings (machine draft, pending review). */
const CATEGORY_HEADINGS_FR: Record<DirectoryCategory, CategoryHeading> = {
  Visa:        { pre: "SPÉCIALISTES DU", italic: "visa et de l’immigration", post: "AU VIETNAM" },
  Legal:       { pre: "AVOCATS",         italic: "et conseil juridique",      post: "AU VIETNAM" },
  Finance:     { pre: "COMPTABLES",      italic: "et conseillers fiscaux",    post: "AU VIETNAM" },
  Property:    { pre: "SPÉCIALISTES DE", italic: "l’immobilier",              post: "AU VIETNAM" },
  Business:    { pre: "CRÉATION",        italic: "d’entreprise",              post: "AU VIETNAM" },
  Audit:       { pre: "CABINETS",        italic: "d’audit et de conformité",  post: "AU VIETNAM" },
  Notary:      { pre: "SERVICES",        italic: "notariaux et légalisation", post: "AU VIETNAM" },
  Translation: { pre: "TRADUCTEURS",     italic: "et interprètes",            post: "AU VIETNAM" },
  Insurance:   { pre: "CONSEILLERS EN",  italic: "assurance",                 post: "AU VIETNAM" },
  Relocation:  { pre: "SPÉCIALISTES DU", italic: "déménagement",              post: "AU VIETNAM" },
  Permit:      { pre: "SERVICES DE",     italic: "permis et licences",        post: "AU VIETNAM" },
  Office:      { pre: "FOURNISSEURS DE", italic: "bureaux et coworking",      post: "AU VIETNAM" },
  HR:          { pre: "SERVICES DE",     italic: "RH et paie",                post: "AU VIETNAM" },
};

// Chinese puts the whole heading in `pre`: the page joins the three parts with
// a space, and a space between Han characters is wrong (style guide §2). The
// empty `italic` and `post` leave only trailing whitespace, which collapses.
// Same words as `TITLES_ZH_HANS` in `seo/category-meta.ts`. MACHINE DRAFT.
const CATEGORY_HEADINGS_ZH_HANS: Record<DirectoryCategory, CategoryHeading> = {
  Visa:        { pre: "越南签证与移民代理",         italic: "", post: "" },
  Legal:       { pre: "越南律师与法律顾问",         italic: "", post: "" },
  Finance:     { pre: "越南会计师与税务顾问",       italic: "", post: "" },
  Property:    { pre: "越南房地产经纪与顾问",       italic: "", post: "" },
  Business:    { pre: "越南公司设立与企业登记",     italic: "", post: "" },
  Audit:       { pre: "越南审计与合规机构",         italic: "", post: "" },
  Notary:      { pre: "越南公证与认证服务",         italic: "", post: "" },
  Translation: { pre: "越南翻译与口译服务",         italic: "", post: "" },
  Insurance:   { pre: "越南保险经纪与顾问",         italic: "", post: "" },
  Relocation:  { pre: "越南搬迁服务公司",           italic: "", post: "" },
  Permit:      { pre: "越南许可与执照办理服务",     italic: "", post: "" },
  Office:      { pre: "越南服务式办公室与联合办公", italic: "", post: "" },
  HR:          { pre: "越南人力资源与薪资服务",     italic: "", post: "" },
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const CATEGORY_HEADINGS_ZH_HANT: Record<DirectoryCategory, CategoryHeading> = {
  Visa:        { pre: "越南簽證與移民代理",         italic: "", post: "" },
  Legal:       { pre: "越南律師與法律顧問",         italic: "", post: "" },
  Finance:     { pre: "越南會計師與稅務顧問",       italic: "", post: "" },
  Property:    { pre: "越南房地產經紀與顧問",       italic: "", post: "" },
  Business:    { pre: "越南公司設立與企業登記",     italic: "", post: "" },
  Audit:       { pre: "越南審計與合規機構",         italic: "", post: "" },
  Notary:      { pre: "越南公證與認證服務",         italic: "", post: "" },
  Translation: { pre: "越南翻譯與口譯服務",         italic: "", post: "" },
  Insurance:   { pre: "越南保險經紀與顧問",         italic: "", post: "" },
  Relocation:  { pre: "越南搬遷服務公司",           italic: "", post: "" },
  Permit:      { pre: "越南許可與執照辦理服務",     italic: "", post: "" },
  Office:      { pre: "越南服務式辦公室與聯合辦公", italic: "", post: "" },
  HR:          { pre: "越南人力資源與薪資服務",     italic: "", post: "" },
};

/** Locale-aware search-page category heading (VI, FR and zh machine drafts). */
export function getCategoryHeading(cat: DirectoryCategory, locale: string): CategoryHeading {
  return byLocale(locale, {
    en: CATEGORY_HEADINGS,
    vi: CATEGORY_HEADINGS_VI,
    fr: CATEGORY_HEADINGS_FR,
    "zh-Hans": CATEGORY_HEADINGS_ZH_HANS,
    "zh-Hant": CATEGORY_HEADINGS_ZH_HANT,
  })[cat];
}

// ── Home page category pills ────────────────────────────────────────────────

// `iconKey` is the stable handle for the icon map (page.tsx PILL_ICONS), the
// same arrangement HOMEPAGE_SERVICE_SHORTCUTS uses below. It does NOT change
// across locales, unlike `label` — so the glyphs cannot silently drop out on
// /vi the way a label-keyed map would.
export const CATEGORY_HOME_PILLS: { iconKey: string; label: string; href: string }[] = [
  { iconKey: "visa",        label: "Visa Services",       href: "/search?category=Visa" },
  { iconKey: "legal",       label: "Legal Counsel",       href: "/search?category=Legal" },
  { iconKey: "finance",     label: "Finance & Accounting", href: "/search?category=Finance" },
  { iconKey: "property",    label: "Real Estate",         href: "/search?category=Property" },
  { iconKey: "business",    label: "Business Setup",      href: "/search?category=Business" },
  { iconKey: "audit",       label: "Audit & Compliance",  href: "/search?category=Audit" },
  { iconKey: "notary",      label: "Notary Services",     href: "/search?category=Notary" },
  { iconKey: "translation", label: "Translation",         href: "/search?category=Translation" },
  { iconKey: "insurance",   label: "Insurance",           href: "/search?category=Insurance" },
  { iconKey: "relocation",  label: "Relocation",          href: "/search?category=Relocation" },
  // Promoted 2026-09-20 (owner) once each held listings.
  { iconKey: "permit",      label: "Permits & Licences",  href: "/search?category=Permit" },
  { iconKey: "hr",          label: "HR & Payroll",        href: "/search?category=HR" },
];

// VI labels keyed by the EN label (reorder-safe). MACHINE DRAFT.
/** French pill labels, keyed by the EN label like the VI map. */
const CATEGORY_HOME_PILLS_FR: Record<string, string> = {
  "Visa Services": "Services de visa",
  "Legal Counsel": "Conseil juridique",
  "Finance & Accounting": "Finance et comptabilité",
  "Real Estate": "Immobilier",
  "Business Setup": "Création d'entreprise",
  "Audit & Compliance": "Audit et conformité",
  "Notary Services": "Services notariaux",
  "Translation": "Traduction",
  "Insurance": "Assurance",
  "Relocation": "Déménagement",
  "Permits & Licences": "Permis et licences",
  "HR & Payroll": "RH et paie",
};

const CATEGORY_HOME_PILLS_VI: Record<string, string> = {
  "Visa Services":        "Dịch vụ Visa",
  "Legal Counsel":        "Tư vấn pháp lý",
  "Finance & Accounting": "Tài chính & Kế toán",
  "Real Estate":          "Bất động sản",
  "Business Setup":       "Thành lập doanh nghiệp",
  "Audit & Compliance":   "Kiểm toán & Tuân thủ",
  "Notary Services":      "Dịch vụ công chứng",
  "Translation":          "Biên dịch",
  "Insurance":            "Bảo hiểm",
  "Relocation":           "Hỗ trợ định cư",
  "Permits & Licences":   "Giấy phép",
  "HR & Payroll":         "Nhân sự & Tiền lương",
};

/** Simplified Chinese pill labels, keyed by the EN label. MACHINE DRAFT. */
const CATEGORY_HOME_PILLS_ZH_HANS: Record<string, string> = {
  "Visa Services":        "签证服务",
  "Legal Counsel":        "法律咨询",
  "Finance & Accounting": "财务与会计",
  "Real Estate":          "房地产",
  "Business Setup":       "公司设立",
  "Audit & Compliance":   "审计与合规",
  "Notary Services":      "公证服务",
  "Translation":          "翻译",
  "Insurance":            "保险",
  "Relocation":           "搬迁服务",
  "Permits & Licences":   "许可与执照",
  "HR & Payroll":         "人力资源与薪资",
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const CATEGORY_HOME_PILLS_ZH_HANT: Record<string, string> = {
  "Visa Services":        "簽證服務",
  "Legal Counsel":        "法律諮詢",
  "Finance & Accounting": "財務與會計",
  "Real Estate":          "房地產",
  "Business Setup":       "公司設立",
  "Audit & Compliance":   "審計與合規",
  "Notary Services":      "公證服務",
  "Translation":          "翻譯",
  "Insurance":            "保險",
  "Relocation":           "搬遷服務",
  "Permits & Licences":   "許可與執照",
  "HR & Payroll":         "人力資源與薪資",
};

/** Home category pills with locale-aware labels (href/iconKey stable). */
export function getCategoryHomePills(locale: string): typeof CATEGORY_HOME_PILLS {
  return CATEGORY_HOME_PILLS.map((p) => ({
    ...p,
    label: localeLabel(locale, p.label, p.label, {
      vi: CATEGORY_HOME_PILLS_VI,
      fr: CATEGORY_HOME_PILLS_FR,
      "zh-Hans": CATEGORY_HOME_PILLS_ZH_HANS,
      "zh-Hant": CATEGORY_HOME_PILLS_ZH_HANT,
    }),
  }));
}

// ── Home page "popular services" shortcuts (curated editorial subset) ───────
// Plain-language, need-framed entry points shown in the feature block beneath
// the hero. Unlike CATEGORY_HOME_PILLS these are NOT 1:1 with the taxonomy:
// two intentionally point at the same `Visa` category, disambiguated by a
// pre-filled free-text `q`. `category` MUST be a canonical DirectoryCategory
// so each link lands on a real filtered results page; `q` narrows further via
// the search page's free-text match.
// `iconKey` is the stable handle for the icon map (page.tsx SHORTCUT_ICONS) and
// the VI label lookup — it does NOT change across locales, unlike `label`.
export const HOMEPAGE_SERVICE_SHORTCUTS: {
  iconKey: string;
  label: string;
  category?: DirectoryCategory; // omit to search across all categories
  q?: string;
}[] = [
  { iconKey: "trc",        label: "TRC and work permit",          category: "Visa",     q: "work permit" },
  { iconKey: "business",   label: "Business setup",               category: "Business" },
  { iconKey: "legal",      label: "Legal counsel",                category: "Legal" },
  { iconKey: "accounting", label: "Accountants and tax services", category: "Finance" },
  { iconKey: "spouse",     label: "Spouse and dependant visa",                          q: "dependant visa" },
];

// VI shortcut labels keyed by iconKey. MACHINE DRAFT.
const SERVICE_SHORTCUT_LABELS_VI: Record<string, string> = {
  trc:        "Thẻ tạm trú và giấy phép lao động",
  business:   "Thành lập doanh nghiệp",
  legal:      "Tư vấn pháp lý",
  accounting: "Dịch vụ kế toán và thuế",
  spouse:     "Visa vợ/chồng và người phụ thuộc",
};

// FR shortcut labels keyed by iconKey. MACHINE DRAFT.
const SERVICE_SHORTCUT_LABELS_FR: Record<string, string> = {
  trc:        "Carte de séjour (TRC) et permis de travail",
  business:   "Création d’entreprise",
  legal:      "Conseil juridique",
  accounting: "Comptables et services fiscaux",
  spouse:     "Visa de conjoint et de personne à charge",
};

// Simplified Chinese shortcut labels keyed by iconKey. MACHINE DRAFT.
const SERVICE_SHORTCUT_LABELS_ZH_HANS: Record<string, string> = {
  trc:        "临时居留卡（TRC）与工作许可证",
  business:   "公司设立",
  legal:      "法律咨询",
  accounting: "会计与税务服务",
  spouse:     "配偶及家属签证",
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const SERVICE_SHORTCUT_LABELS_ZH_HANT: Record<string, string> = {
  trc:        "臨時居留卡（TRC）與工作許可證",
  business:   "公司設立",
  legal:      "法律諮詢",
  accounting: "會計與稅務服務",
  spouse:     "配偶及家屬簽證",
};

/** Popular-service shortcuts with locale-aware labels (category/q/iconKey stable). */
export function getServiceShortcuts(locale: string): typeof HOMEPAGE_SERVICE_SHORTCUTS {
  return HOMEPAGE_SERVICE_SHORTCUTS.map((s) => ({
    ...s,
    label: localeLabel(locale, s.iconKey, s.label, {
      vi: SERVICE_SHORTCUT_LABELS_VI,
      fr: SERVICE_SHORTCUT_LABELS_FR,
      "zh-Hans": SERVICE_SHORTCUT_LABELS_ZH_HANS,
      "zh-Hant": SERVICE_SHORTCUT_LABELS_ZH_HANT,
    }),
  }));
}

// ── Footer directory links ──────────────────────────────────────────────────

// Keyed by CATEGORY, not by href (2026-09-18): the address is decided by
// `categoryHref` in `landing-pages.ts`, which sends Legal, Finance and Business
// to their landing pages and every other category to the filtered search. It is
// applied in `Footer.tsx` rather than here because `landing-pages.ts` imports
// modules that import this file, and a cycle evaluated at module load hands one
// side an undefined constant.
export const CATEGORY_FOOTER_LINKS: { category: DirectoryCategory; label: string }[] = [
  { category: "Legal",       label: "Legal Services" },
  { category: "Finance",     label: "Accounting & Tax" },
  { category: "Visa",        label: "Visa & Immigration" },
  { category: "Property",    label: "Real Estate" },
  { category: "Business",    label: "Business Setup" },
  { category: "Insurance",   label: "Insurance" },
  { category: "Audit",       label: "Audit & Compliance" },
  { category: "Notary",      label: "Notary & Legalization" },
  { category: "Translation", label: "Translation & Interpretation" },
  { category: "Relocation",  label: "Relocation" },
  { category: "Permit",      label: "Permits & Licences" },
  { category: "HR",          label: "HR & Payroll" },
];

// VI footer labels keyed by the EN label (reorder-safe). MACHINE DRAFT.
const CATEGORY_FOOTER_LINKS_FR: Record<string, string> = {
  "Legal Services": "Services juridiques",
  "Accounting & Tax": "Comptabilité et fiscalité",
  "Visa & Immigration": "Visa et immigration",
  "Real Estate": "Immobilier",
  "Business Setup": "Création d'entreprise",
  "Insurance": "Assurance",
  "Audit & Compliance": "Audit et conformité",
  "Notary & Legalization": "Notariat et légalisation",
  "Translation & Interpretation": "Traduction et interprétation",
  "Relocation": "Déménagement",
  "Permits & Licences": "Permis et licences",
  "HR & Payroll": "RH et paie",
};

const CATEGORY_FOOTER_LINKS_VI: Record<string, string> = {
  "Legal Services":               "Dịch vụ pháp lý",
  "Accounting & Tax":             "Kế toán & Thuế",
  "Visa & Immigration":           "Visa & Di trú",
  "Real Estate":                  "Bất động sản",
  "Business Setup":               "Thành lập doanh nghiệp",
  "Audit & Compliance":           "Kiểm toán & Tuân thủ",
  "Notary & Legalization":        "Công chứng & Hợp pháp hóa",
  "Translation & Interpretation": "Biên dịch & Phiên dịch",
  "Insurance":                    "Bảo hiểm",
  "Relocation":                   "Hỗ trợ định cư",
  "Permits & Licences":           "Giấy phép",
  "HR & Payroll":                 "Nhân sự & Tiền lương",
};

// Simplified Chinese footer labels keyed by the EN label. MACHINE DRAFT.
const CATEGORY_FOOTER_LINKS_ZH_HANS: Record<string, string> = {
  "Legal Services":               "法律服务",
  "Accounting & Tax":             "会计与税务",
  "Visa & Immigration":           "签证与移民",
  "Real Estate":                  "房地产",
  "Business Setup":               "公司设立",
  "Insurance":                    "保险",
  "Audit & Compliance":           "审计与合规",
  "Notary & Legalization":        "公证与认证",
  "Translation & Interpretation": "翻译与口译",
  "Relocation":                   "搬迁服务",
  "Permits & Licences":           "许可与执照",
  "HR & Payroll":                 "人力资源与薪资",
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const CATEGORY_FOOTER_LINKS_ZH_HANT: Record<string, string> = {
  "Legal Services":               "法律服務",
  "Accounting & Tax":             "會計與稅務",
  "Visa & Immigration":           "簽證與移民",
  "Real Estate":                  "房地產",
  "Business Setup":               "公司設立",
  "Insurance":                    "保險",
  "Audit & Compliance":           "審計與合規",
  "Notary & Legalization":        "公證與認證",
  "Translation & Interpretation": "翻譯與口譯",
  "Relocation":                   "搬遷服務",
  "Permits & Licences":           "許可與執照",
  "HR & Payroll":                 "人力資源與薪資",
};

/** Footer directory links with locale-aware labels. The caller turns each
 *  category into an address with `categoryHref`. */
export function getCategoryFooterLinks(locale: string): { category: DirectoryCategory; label: string }[] {
  return CATEGORY_FOOTER_LINKS.map((l) => ({
    ...l,
    label: localeLabel(locale, l.label, l.label, {
      vi: CATEGORY_FOOTER_LINKS_VI,
      fr: CATEGORY_FOOTER_LINKS_FR,
      "zh-Hans": CATEGORY_FOOTER_LINKS_ZH_HANS,
      "zh-Hant": CATEGORY_FOOTER_LINKS_ZH_HANT,
    }),
  }));
}

// ── Application form categories ─────────────────────────────────────────────

export const APPLICATION_CATEGORIES = [
  "Legal Counsel & Representation",
  "Business Registration & Licensing",
  "Taxation & Accounting",
  "Visa & Immigration Services",
  "Real Estate Advisory",
  "Financial Planning & Wealth",
  // Renamed from "HR & Recruitment" on 2026-09-19, when HR got its own shelf
  // (it used to file under Business). No application or draft had ever used
  // the old value, so nothing stored carries it.
  "HR & Payroll",
  "Audit & Compliance",
  "Notary & Legalization",
  "Translation & Interpretation",
  "Insurance Services",
  "Relocation Services",
  // 2026-08-27. Deliberately NOT folded into "Business Registration &
  // Licensing": that one is company formation, and this is the practitioner
  // who obtains permits and licences as the service itself — work permits,
  // construction permits, sector licences.
  "Permits & Licensing",
  // 2026-09-13. Serviced and virtual offices, coworking, office leasing.
  "Office & Workspace",
  "Other",
] as const;

export type ApplicationCategory = (typeof APPLICATION_CATEGORIES)[number];

// Locale-aware labels for the apply-form category dropdown. The VALUE stays
// the English string above (it's stored on the application + drives the
// directory mapping); only the visible label localizes. VI is a MACHINE DRAFT
// pending the native reviewer; FR falls back to EN.
const APPLICATION_CATEGORY_LABELS_VI: Record<ApplicationCategory, string> = {
  "Legal Counsel & Representation":    "Tư vấn và đại diện pháp lý",
  "Business Registration & Licensing": "Đăng ký và cấp phép kinh doanh",
  "Taxation & Accounting":             "Thuế và kế toán",
  "Visa & Immigration Services":       "Dịch vụ thị thực và di trú",
  "Real Estate Advisory":              "Tư vấn bất động sản",
  "Financial Planning & Wealth":       "Hoạch định tài chính và tài sản",
  "HR & Payroll":                      "Nhân sự và tiền lương",
  "Audit & Compliance":                "Kiểm toán và tuân thủ",
  "Notary & Legalization":             "Công chứng và hợp pháp hóa",
  "Translation & Interpretation":      "Biên dịch và phiên dịch",
  "Insurance Services":                "Dịch vụ bảo hiểm",
  "Relocation Services":               "Dịch vụ hỗ trợ định cư",
  "Permits & Licensing":               "Giấy phép và cấp phép",
  "Office & Workspace":                "Văn phòng làm việc",
  "Other":                             "Khác",
};

// FR apply-form labels. MACHINE DRAFT.
const APPLICATION_CATEGORY_LABELS_FR: Record<ApplicationCategory, string> = {
  "Legal Counsel & Representation":    "Conseil et représentation juridiques",
  "Business Registration & Licensing": "Immatriculation et licences d’entreprise",
  "Taxation & Accounting":             "Fiscalité et comptabilité",
  "Visa & Immigration Services":       "Services de visa et d’immigration",
  "Real Estate Advisory":              "Conseil immobilier",
  "Financial Planning & Wealth":       "Planification financière et gestion de patrimoine",
  "HR & Payroll":                      "RH et paie",
  "Audit & Compliance":                "Audit et conformité",
  "Notary & Legalization":             "Notariat et légalisation",
  "Translation & Interpretation":      "Traduction et interprétation",
  "Insurance Services":                "Services d’assurance",
  "Relocation Services":               "Services de relocation",
  "Permits & Licensing":               "Permis et licences",
  "Office & Workspace":                "Bureaux et espaces de travail",
  "Other":                             "Autre",
};

// Simplified Chinese apply-form labels. MACHINE DRAFT.
const APPLICATION_CATEGORY_LABELS_ZH_HANS: Record<ApplicationCategory, string> = {
  "Legal Counsel & Representation":    "法律咨询与代理",
  "Business Registration & Licensing": "企业登记与经营许可",
  "Taxation & Accounting":             "税务与会计",
  "Visa & Immigration Services":       "签证与移民服务",
  "Real Estate Advisory":              "房地产咨询",
  "Financial Planning & Wealth":       "财务规划与财富管理",
  "HR & Payroll":                      "人力资源与薪资",
  "Audit & Compliance":                "审计与合规",
  "Notary & Legalization":             "公证与认证",
  "Translation & Interpretation":      "翻译与口译",
  "Insurance Services":                "保险服务",
  "Relocation Services":               "搬迁服务",
  "Permits & Licensing":               "许可与执照办理",
  "Office & Workspace":                "办公室与办公空间",
  "Other":                             "其他",
};

// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
const APPLICATION_CATEGORY_LABELS_ZH_HANT: Record<ApplicationCategory, string> = {
  "Legal Counsel & Representation":    "法律諮詢與代理",
  "Business Registration & Licensing": "企業登記與經營許可",
  "Taxation & Accounting":             "稅務與會計",
  "Visa & Immigration Services":       "簽證與移民服務",
  "Real Estate Advisory":              "房地產諮詢",
  "Financial Planning & Wealth":       "財務規劃與財富管理",
  "HR & Payroll":                      "人力資源與薪資",
  "Audit & Compliance":                "審計與合規",
  "Notary & Legalization":             "公證與認證",
  "Translation & Interpretation":      "翻譯與口譯",
  "Insurance Services":                "保險服務",
  "Relocation Services":               "搬遷服務",
  "Permits & Licensing":               "許可與執照辦理",
  "Office & Workspace":                "辦公室與辦公空間",
  "Other":                             "其他",
};

/**
 * Apply-form category options as `{ value, label }`. Value is the canonical
 * English string (stored/submitted); label localizes (VI, FR and zh machine drafts).
 */
export function getApplicationCategories(locale: string): { value: ApplicationCategory; label: string }[] {
  return APPLICATION_CATEGORIES.map((c) => ({
    value: c,
    label: localeLabel(locale, c, c, {
      vi: APPLICATION_CATEGORY_LABELS_VI,
      fr: APPLICATION_CATEGORY_LABELS_FR,
      "zh-Hans": APPLICATION_CATEGORY_LABELS_ZH_HANS,
      "zh-Hant": APPLICATION_CATEGORY_LABELS_ZH_HANT,
    }),
  }));
}

// ── Application → directory mapping (used on admin approval) ────────────────

export const APPLICATION_TO_DIRECTORY: Record<ApplicationCategory, DirectoryCategory> = {
  "Legal Counsel & Representation":    "Legal",
  "Business Registration & Licensing": "Business",
  "Taxation & Accounting":             "Finance",
  "Visa & Immigration Services":       "Visa",
  "Real Estate Advisory":              "Property",
  "Financial Planning & Wealth":       "Finance",
  "HR & Payroll":                      "HR",
  "Audit & Compliance":                "Audit",
  "Notary & Legalization":             "Notary",
  "Translation & Interpretation":      "Translation",
  "Insurance Services":                "Insurance",
  "Relocation Services":               "Relocation",
  "Permits & Licensing":               "Permit",
  "Office & Workspace":                "Office",
  "Other":                             "Business",
};
